import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Dialog,
    Paper,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select, IconButton, Tooltip, DialogContent, DialogActions
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import CreateUpdateModal from "./createUpdateModal";
import {
    useCreateChargeV2Mutation,
    useDeleteChargeV2Mutation,
    useGetChargesTypesV2Query,
    useUpdateChargeV2Mutation
} from "../../../core/api/charge-api";
import { useParams } from "react-router-dom";

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const ChargeManagement = (props) => {
    const [charge, setCharge] = useState();
    const [createMode, setCreateMode] = useState(false);
    const [updateMode, setUpdateMode] = useState(false);
    const [deleteMode, setDeleteMode] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [filteredData, setFilteredData] = useState([]);

    const [createCharge] = useCreateChargeV2Mutation();
    const [updateCharge] = useUpdateChargeV2Mutation();
    const [deleteCharge] = useDeleteChargeV2Mutation();

    const params = useParams();

    const {
        data,
        isLoading,
        isSuccess,
        isError,
        error
    } = useGetChargesTypesV2Query(params.game);

    // Extract unique types and categories for filters
    const uniqueTypes = isSuccess ? [...new Set(data.map(item => item.type))] : [];
    const uniqueCategories = isSuccess ? [...new Set(data.map(item => item.category))] : [];

    // Apply filters and search whenever data or filter values change
    useEffect(() => {
        if (isSuccess && data) {
            let filtered = [...data];

            // Apply search by title
            if (searchQuery) {
                filtered = filtered.filter(item =>
                    item.title.toLowerCase().includes(searchQuery.toLowerCase())
                );
            }

            // Apply type filter
            if (typeFilter) {
                filtered = filtered.filter(item => item.type === typeFilter);
            }

            // Apply category filter
            if (categoryFilter) {
                filtered = filtered.filter(item => item.category === categoryFilter);
            }

            setFilteredData(filtered);
        }
    }, [data, isSuccess, searchQuery, typeFilter, categoryFilter]);

    const handleCreateCharge = () => {
        setCharge({
            type: "",
            category: "",
            key: "",
            title: "",
            consumable: "",
            customFields: []
        });
        setCreateMode(true);
    };

    const handleCancel = () => {
        setCreateMode(false);
        setUpdateMode(false);
        setDeleteMode(false);
    };

    const handleChargeAction = () => {
        if (createMode) {
            createCharge({data: charge, game: params.game});
        }
        if (updateMode) {
            updateCharge({
                data: {
                    category: charge.category,
                    chargeType: charge.chargeType,
                    consumable: charge.consumable,
                    customFields: charge.customFields,
                    id: charge.id,
                    key: charge.key,
                    title: charge.title,
                    type: charge.type
                },
                game: params.game
            });
        }
        handleCancel();
    };

    const handleEditCharge = (item) => {
        setCharge(item);
        setUpdateMode(true);
    };

    const handleDeleteCharge = (item) => {
        setCharge(item);
        setDeleteMode(true);
    };

    const handleDeleteAction = () => {
        deleteCharge({id: charge.id, game: params.game});
        handleCancel();
    };

    // DataGrid columns configuration
    const columns = [
        { field: 'type', headerName: 'Group', flex: 1, sortable: true },
        { field: 'chargeType', headerName: 'Type', flex: 1, sortable: true },
        { field: 'category', headerName: 'Category', flex: 1, sortable: true },
        { field: 'key', headerName: 'ID', flex: 1, sortable: true },
        { field: 'title', headerName: 'Title', flex: 1, sortable: true },
        {
            field: 'consumable',
            headerName: 'Consumable',
            flex: 1,
            sortable: true,
            renderCell: (params) => params.value ? 'Yes' : 'No'
        },
        {
            field: 'customFieldsDisplayName',
            headerName: 'Custom Fields',
            flex: 1,
            sortable: true
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Edit">
                        <IconButton
                            onClick={() => handleEditCharge(params.row)}
                            color="primary"
                            size="small"
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            onClick={() => handleDeleteCharge(params.row)}
                            color="error"
                            size="small"
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    ];

    return (
        <>
            <h1>Charge Management</h1>

            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
                <Button variant="contained" onClick={handleCreateCharge}>
                    Add Charge
                </Button>

                <TextField
                    label="Search by Title"
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />

                <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="type-filter-label">Filter by Group</InputLabel>
                    <Select
                        labelId="type-filter-label"
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        label="Filter by Group"
                    >
                        <MenuItem value="">
                            <em>All</em>
                        </MenuItem>
                        {uniqueTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                                {type}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="category-filter-label">Filter by Category</InputLabel>
                    <Select
                        labelId="category-filter-label"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        label="Filter by Category"
                    >
                        <MenuItem value="">
                            <em>All</em>
                        </MenuItem>
                        {uniqueCategories.map((category) => (
                            <MenuItem key={category} value={category}>
                                {category}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Paper sx={{ height: 'calc(100vh - 200px)', width: '100%' }}>
                <DataGrid
                    rows={filteredData}
                    columns={columns}
                    loading={isLoading}
                    disableColumnSelector
                    disableDensitySelector
                    getRowId={(row) => row.id}
                    components={{
                        Toolbar: GridToolbar,
                    }}
                    componentsProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                        },
                    }}
                />
            </Paper>

            <Dialog open={createMode || updateMode} onClose={handleCancel}>
                <CreateUpdateModal
                    handleCancel={handleCancel}
                    handleChargeAction={handleChargeAction}
                    setCharge={setCharge}
                    charge={charge}
                />
            </Dialog>

            <Dialog open={deleteMode} onClose={handleCancel}>
                <DialogContent>
                    <h1>Are you sure you want to delete charge {charge?.title}?</h1>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={handleDeleteAction}>
                        Delete
                    </Button>
                    <Button variant="outlined" onClick={handleCancel}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ChargeManagement;